// Where the findings a Reviews list opens cite, as turns of their sessions:
// the number the Lanes axis prints for each cited message, and the length of
// the axis those numbers run along.
//
// The numbers come from the record itself, through the read the lanes use
// (conversationRead.readConversationPage): its newest page first, and the
// pages before it only while a cited message is still missing. The lanes
// print a turn as the messages before the loaded pages plus its index among
// them, counting from zero, so a message's number is the record's total less
// the turns loaded, plus its place in them. That holds however many pages
// are loaded, so a number never moves when an earlier page arrives.
//
// The analytics message entity cannot stand in for the record: the record
// places a message by its latest observation and the message index's own
// order, which interleaves histories that the entity's event time and source
// ordinal order apart.
import { readConversationPage } from "./conversationRead.js";

// Sessions an opened case draws, and the pages of one session's record the
// placement reads at most: the newest and the ones before it, while a cited
// message is still missing.
export const TIMELINE_SESSIONS = 3;
export const TIMELINE_RECORD_PAGES = 3;

const NIL_ID = "00000000-0000-0000-0000-000000000000";
const idOf = (v) => {
    const id = String(v ?? "").trim().toLowerCase();
    return id && id !== NIL_ID ? id : null;
};

// The record read's window: ninety days back from an hour ahead, wide enough
// to hold a whole session the way the lanes read it from its first message.
export function recordWindow(now = Date.now()) {
    const end = Math.ceil(now / 60_000) * 60_000 + 3600000;
    return { absolute: { start: new Date(end - 90 * 86400000).toISOString(), end: new Date(end).toISOString() } };
}

// A harness or a test answers for the record:
// { [session]: { total, turns: { [message id]: turn } } }, turns numbered as
// the axis prints them. `delayMs` holds each answer back, so a harness can
// show the placing state.
let SEEDED = null;
let SEED_DELAY_MS = 0;
export function seedCitedTurns(bySession, { delayMs = 0 } = {}) {
    SEEDED = bySession ? new Map(Object.entries(bySession)) : null;
    SEED_DELAY_MS = delayMs;
}

// Per session, the record as far as placement has read it, for the page's
// lifetime: the loaded turns' message ids oldest first, the record's total,
// and where the page before them starts. Another case or tab that opens the
// same session reads nothing new unless it cites an older message.
const records = new Map();
export function _clearCitedTurnsForTests() {
    records.clear();
}

function recordOf(session) {
    if (!records.has(session)) records.set(session, { ids: [], index: new Map(), total: null, cursor: null, exhausted: false, pages: 0, reading: null });
    return records.get(session);
}

// One more page, oldest-first, in front of the ones already loaded. Reads on
// one session run one at a time; a failed or aborted read leaves the record
// as it was.
function readEarlier(entry, session, readPage, signal) {
    if (!entry.reading) {
        entry.reading = readPage({ traceId: session, timeRange: recordWindow(), signal, cursor: entry.cursor })
            .then((page) => {
                const ids = (Array.isArray(page?.turns) ? page.turns : []).map((t) => idOf(t?.messageId) || "");
                if (!entry.pages && Number.isFinite(Number(page?.total)) && Number(page.total) > 0) entry.total = Number(page.total);
                entry.ids = [...ids, ...entry.ids];
                entry.index = new Map(entry.ids.map((id, i) => [id, i]));
                entry.cursor = page?.nextCursor || null;
                entry.exhausted = Boolean(page?.exhausted) || !entry.cursor;
                entry.pages += 1;
            })
            .finally(() => { entry.reading = null; });
    }
    return entry.reading;
}

// The axis's length and the number of the first loaded turn. The lanes count
// the turns before the loaded pages from the record's total; a record whose
// total could not be read is numbered from its loaded turns alone once it is
// whole, and not at all before that.
function axisOf(entry) {
    const loaded = entry.ids.length;
    const total = entry.total != null ? Math.max(entry.total, loaded) : entry.exhausted ? loaded : null;
    return { total, base: total != null ? total - loaded : null };
}

// -> { total, turns: Map(message id -> turn), truncated }. `truncated` says
// the read stopped at its page bound with a cited message still unread, so
// it stayed unplaced; a message the whole record does not hold is simply not
// placed.
export async function fetchSessionTurns({ session, messageIds, signal = null, readPage = readConversationPage }) {
    const id = idOf(session);
    const wanted = [...new Set((messageIds || []).map(idOf).filter(Boolean))];
    if (!id || !wanted.length) return { total: null, turns: new Map(), truncated: false };
    if (SEEDED) {
        if (SEED_DELAY_MS) await new Promise((resolve) => setTimeout(resolve, SEED_DELAY_MS));
        const seed = SEEDED.get(id) || null;
        return { total: seed?.total ?? null, turns: new Map(Object.entries(seed?.turns || {})), truncated: false };
    }
    const entry = recordOf(id);
    const missing = () => wanted.some((m) => !entry.index.has(m));
    while (missing() && !entry.exhausted && entry.pages < TIMELINE_RECORD_PAGES) {
        try {
            await readEarlier(entry, id, readPage, signal);
        } catch (error) {
            // Another caller's read, given up by its own signal: read again under ours.
            if (error?.name === "AbortError" && !signal?.aborted) continue;
            throw error;
        }
    }
    const { total, base } = axisOf(entry);
    const turns = new Map();
    if (total != null) {
        for (const m of wanted) {
            if (entry.index.has(m)) turns.set(m, base + entry.index.get(m));
        }
    }
    return { total, turns, truncated: missing() && !entry.exhausted };
}

// The message at a printed turn of a session placement has read, or null:
// how a turn number the triage agent quoted becomes a message the detail
// panel can open at.
export function recordMessageAt(session, turn) {
    const entry = records.get(idOf(session));
    const base = entry ? axisOf(entry).base : null;
    if (base == null || !Number.isInteger(turn)) return null;
    return entry.ids[turn - base] || null;
}

// A case's findings by session, the busiest first, each finding's citations
// in order: { sessions: [{ session, findings: [finding] }], rest } where rest
// counts the sessions beyond the ones drawn.
export function caseSessions(findings = [], limit = TIMELINE_SESSIONS) {
    const bySession = new Map();
    for (const f of findings) {
        const s = idOf(f?.session);
        if (!s) continue;
        if (!bySession.has(s)) bySession.set(s, []);
        bySession.get(s).push(f);
    }
    const all = [...bySession.entries()].map(([session, list]) => ({ session, findings: list }))
        .sort((a, b) => b.findings.length - a.findings.length || a.session.localeCompare(b.session));
    return { sessions: all.slice(0, limit), rest: Math.max(0, all.length - limit) };
}

// The first and last turn a finding cites, or null when none was placed.
export function findingSpan(finding, turns) {
    const placed = (finding?.citations || []).map((c) => turns?.get(c.messageId)).filter((t) => Number.isFinite(t));
    if (!placed.length) return null;
    return { from: Math.min(...placed), to: Math.max(...placed) };
}

// Findings binned along a session's turns, numbered from zero as the axis
// prints them: `bins` buckets, each holding the findings that cite a turn
// inside it, a finding once per bucket. Turn t fills the stretch
// [t / total, (t + 1) / total) of the axis, so a bucket covers the turns whose
// stretch starts inside it; in a session shorter than the bucket count some
// buckets cover none (from > to) and never fill.
export function binFindings(findings = [], turns, total, bins) {
    const out = Array.from({ length: bins }, (_, i) => ({ index: i, from: Math.ceil((i * total) / bins), to: Math.ceil(((i + 1) * total) / bins) - 1, findings: [] }));
    if (!(total > 0)) return out;
    for (const f of findings) {
        const hit = new Set();
        for (const c of f?.citations || []) {
            const t = turns?.get(c.messageId);
            if (!Number.isFinite(t) || t < 0 || t >= total) continue;
            hit.add(Math.min(bins - 1, Math.floor((t * bins) / total)));
        }
        for (const i of hit) out[i].findings.push(f);
    }
    return out;
}
